import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, { useState } from 'react';
import styled from 'styled-components';

import { ScrubTranscriptStrip } from './ScrubTranscriptStrip';

import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';

/**
 * PLAT-11109 DS1 — the row player, shared by the Reel Editor's Section rows and
 * the Add Clips picker's candidate rows.
 *
 * Three pieces, written once: the reveal-on-hover play/pause on the frame, the
 * scrub track under the row, and the readout above the pointer with the words
 * spoken there. They were PLAT-11073's, declared inside `ReelSectionRow`; the
 * picker needs the same player for a clip it is *about* to add, and a second
 * copy of a pointer-capture scrubber is a copy that drifts the first time
 * either row is touched.
 *
 * What differs between the two rows is parameterised — the length being
 * scrubbed, the words to read out, and whether the readout carries an outer
 * (reel) time as well as the clip's own (DS5). Everything else is identical,
 * including the three traps that made the extraction worth doing: the pointer
 * capture that survives the pointer leaving a 14px strip, the `stopPropagation`
 * on the click the browser synthesises on top of the gesture, and the
 * scrubbing signal a row uses to stand its own drag down.
 *
 * `RowPlayToggle` moved to `common/` once a third feature needed it; re-exported
 * here so the rows that already pair it with the tracker keep one import.
 */

export { RowPlayToggle } from '@src/components/common/RowPlayToggle';

/** How far an arrow key moves the playhead along the tracker. */
const SCRUB_KEY_STEP_SECONDS = 1;

/**
 * Half the readout's width — what keeps it inside the list at the ends.
 *
 * An estimate rather than a measurement: the readout hugs its times line, which
 * is `mm:ss` at a fixed size in tabular figures, so its width does not move.
 * Only the clamp reads this; the box itself is sized by its content.
 */
const SCRUB_READOUT_HALF_WIDTH = 85;

/**
 * How far short of the text column's box the tracker stops.
 *
 * The bar spans its container, so its right edge is already flush with the box
 * the first line is laid out in — but an ellipsized line drops a whole
 * character, so its last glyph stops a few pixels short of that box and the bar
 * reads as overrunning the text. This is that slack — about half a character at
 * `--text-xs` — so the two line up to the eye rather than to the layout.
 */
const PLAY_TRACK_END_INSET = 5;

interface RowPlayTrackerProps {
  /** Length of what is being scrubbed, in seconds. */
  duration: number;
  /** How far playback is through it, 0–1 — what the bar draws when idle. */
  progress: number;
  /**
   * What is said in it, word by word, for the readout's transcript strip.
   * Nothing at all where there is no transcript behind the row — an empty strip
   * would only be a gap under the times.
   */
  words?: ReelSectionWord[];
  /**
   * Where this tracker's zero sits on the words' clock. A Section's words are
   * timed against the reel, so it passes the Section's start; a picker
   * candidate's are already on the clip's own clock, so it passes nothing.
   */
  wordsOffset?: number;
  /**
   * Names a second, outer clock in the readout — "Reel" for a Section, which
   * sits in one. Omitted for a candidate, which sits nowhere yet (DS5): the
   * two-line variant would invent a reel position the creator has not chosen.
   */
  outerLabel?: string;
  /**
   * Whether the times float above the bar as the pointer crosses it. Off where
   * the row already carries them — an open Section says both clocks on its own
   * sub-line, and a second copy of them on a box over the title is the one
   * place on this row a hover may not put something (PLAT-11246 rework).
   *
   * Only the box goes. `onPointerTimeChange` reports either way, so the clocks
   * that replace it still follow the pointer.
   */
  showReadout?: boolean;
  ariaLabel: string;
  testId?: string;
  readoutTestId?: string;
  trackTestId?: string;
  transcriptTestId?: string;
  /** Where along the tracker the creator put playback, in this tracker's clock. */
  onScrub: (localTime: number) => void;
  /**
   * A drag is in flight. A draggable ancestor wins over a pointer drag inside
   * it, so a row that can be dragged stands its own drag down on this —
   * otherwise sliding the playhead along the bar lifts the row out of the list.
   */
  onScrubbingChange?: (isScrubbing: boolean) => void;
  /**
   * Where the pointer sits on the tracker, in this tracker's clock, and `null`
   * once it leaves. A different rule from `onScrub` (PLAT-11188 DS6): the
   * pointer reports as soon as it is *on* the bar, drag or no drag, so a row
   * that reads out somewhere other than the readout — the Section fold — marks
   * the same moment the times above it name.
   */
  onPointerTimeChange?: (localTime: number | null) => void;
  /** Set by `styled(RowPlayTracker)`, which is how a row places the strip. */
  className?: string;
}

/**
 * The strip under a row: the bar, the knob that takes hold of it, and the
 * readout of where a scrub would land.
 */
export const RowPlayTracker: React.FC<RowPlayTrackerProps> = ({
  duration,
  progress,
  words,
  wordsOffset = 0,
  outerLabel,
  showReadout = true,
  ariaLabel,
  testId,
  readoutTestId,
  trackTestId,
  transcriptTestId,
  onScrub,
  onScrubbingChange,
  onPointerTimeChange,
  className,
}) => {
  // Where the pointer sits on the tracker, 0–1. It is what the readout above
  // the bar reads out, and while a drag is in flight it is also what the bar
  // draws — so the fill keeps up with the pointer rather than waiting for the
  // time the player reports back a frame later.
  const [pointerFraction, setPointerFraction] = useState<number | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);

  const setScrubbing = (value: boolean) => {
    setIsScrubbing(value);
    onScrubbingChange?.(value);
  };

  // Reported from the pointer handlers rather than an effect: the value is
  // already an event, and a row that sets state on hearing it would otherwise
  // re-render this tracker back into the same notification.
  const setPointerAt = (fraction: number | null) => {
    setPointerFraction(fraction);
    onPointerTimeChange?.(fraction === null ? null : fraction * duration);
  };

  /** Where along the tracker a pointer event landed, 0–1. */
  const fractionAt = (e: React.PointerEvent<HTMLDivElement>): number => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    if (width <= 0) return 0;
    return Math.min(1, Math.max(0, (e.clientX - left) / width));
  };

  const seekToFraction = (fraction: number) => onScrub(fraction * duration);

  // What the bar draws: the pointer while it is dragging the tracker, the
  // player's own position the rest of the time.
  const fill =
    isScrubbing && pointerFraction !== null ? pointerFraction : progress;

  return (
    <PlayTrackHitArea
      className={className}
      data-testid={testId}
      // The bar is 2px, which is a target no pointer can reliably hit, so the
      // strip around it takes the gesture and the bar just draws.
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(fill * duration)}
      aria-valuetext={convertTimeToMMSS(fill * duration)}
      onPointerDown={e => {
        if (e.button !== 0) return;
        // The row's own click seeks or picks, which is the one thing a scrub is
        // not asking for.
        e.stopPropagation();
        // Capture, so the drag survives the pointer leaving a 14px strip — it
        // will, immediately.
        e.currentTarget.setPointerCapture(e.pointerId);
        setScrubbing(true);
        const fraction = fractionAt(e);
        setPointerAt(fraction);
        seekToFraction(fraction);
      }}
      onPointerMove={e => {
        const fraction = fractionAt(e);
        setPointerAt(fraction);
        if (isScrubbing) seekToFraction(fraction);
      }}
      onPointerUp={() => setScrubbing(false)}
      // The browser releases the capture on `pointerup` itself, so this is for
      // the drag that ends some other way — cancelled by a gesture, or the
      // pointer going away — rather than sticking the bar to a pointer that is
      // no longer there.
      onLostPointerCapture={() => setScrubbing(false)}
      onPointerLeave={() => {
        if (!isScrubbing) setPointerAt(null);
      }}
      // A `pointerdown` + `pointerup` on the tracker still synthesises a click,
      // which would bubble to the row and undo the scrub with a seek back to
      // the row's start — or, in the picker, pick the clip.
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const step =
          ((e.key === 'ArrowLeft' ? -1 : 1) * SCRUB_KEY_STEP_SECONDS) /
          duration;
        seekToFraction(Math.min(1, Math.max(0, fill + step)));
      }}
    >
      {/* The time at the pointer, not at the playhead: this answers "where
          would this land" while the creator is still deciding. */}
      {pointerFraction !== null && showReadout && (
        <ScrubReadout
          data-testid={readoutTestId}
          style={{
            ...MODAL_TOOLTIP_STYLE,
            // Follows the pointer, held far enough inside the tracker's ends
            // that a readout at 0:00 is not clipped by the list.
            left: `clamp(${SCRUB_READOUT_HALF_WIDTH}px, ${
              pointerFraction * 100
            }%, calc(100% - ${SCRUB_READOUT_HALF_WIDTH}px))`,
          }}
        >
          <Times>
            {outerLabel && (
              <>
                <TimeLabel>{outerLabel}</TimeLabel>{' '}
                {convertTimeToMMSS(wordsOffset + pointerFraction * duration)}
                <Separator>·</Separator>
                <TimeLabel>Clip</TimeLabel>{' '}
              </>
            )}
            {convertTimeToMMSS(pointerFraction * duration)}
          </Times>
          {!!words?.length && (
            <ScrubTranscriptStrip
              words={words}
              time={wordsOffset + pointerFraction * duration}
              testId={transcriptTestId}
            />
          )}
        </ScrubReadout>
      )}
      <PlayTrack data-testid={trackTestId}>
        {/* Inline width, not a styled prop: this changes every frame, and a
            prop-driven rule would mint a class per value. */}
        <PlayTrackFill style={{ width: `${fill * 100}%` }} />
      </PlayTrack>
      {/* Outside the bar, which clips its own overflow — the knob has to sit
          half over each end at 0% and 100%. */}
      <ScrubKnob
        $isVisible={pointerFraction !== null}
        style={{ left: `${fill * 100}%` }}
      />
    </PlayTrackHitArea>
  );
};

/**
 * PLAT-11073 — the play tracker's own strip: what the scrub gesture lands in,
 * and where the bar, the knob and the readout are placed from.
 *
 * A 2px rule is not a pointer target, so the bar only draws and this takes the
 * gesture: down to the row's own bottom edge and a little above the bar, and no
 * further — below a Section row is the insert rule's gap, whose `+` it would
 * otherwise cover.
 *
 * Placed against whichever box the row hangs it on — the Section rows' `Body`,
 * so the tracker runs from the thumbnail's left edge to the end of the text and
 * stops clear of the face pile; the picker's row, so it runs the row's width.
 * A row moves it by wrapping this component (`styled(RowPlayTracker)`).
 */
const PlayTrackHitArea = styled.div`
  position: absolute;
  left: 0;
  right: ${PLAY_TRACK_END_INSET}px;
  bottom: -8px;
  height: 14px;
  display: flex;
  align-items: flex-end;
  /* Leaves the bar where it was before the strip was wrapped around it. */
  padding-bottom: 6px;
  cursor: pointer;
  /* A horizontal drag on a touch screen is a scrub, not a scroll of the list. */
  touch-action: none;
  user-select: none;

  /* No focus ring: the strip is the width of the row, so an outline on it reads
     as the whole row being framed — and it lands on the first keypress after a
     scrub, which is usually the space bar going to the player. */
  &:focus-visible {
    outline: none;
  }
`;

/**
 * How far through the row playback is. Gold, like the marking on a Section's
 * handle column: one colour for "this is what you are hearing", said two ways.
 */
const PlayTrack = styled.div`
  position: relative;
  flex: 1;
  height: 2px;
  border-radius: 1px;
  overflow: hidden;
  background-color: var(--border);
`;

const PlayTrackFill = styled.div`
  height: 100%;
  background-color: var(--accent);
`;

/**
 * What says the bar can be taken hold of. Only while the pointer is on the
 * tracker (or dragging it), so the resting row is the bar and nothing else.
 */
const ScrubKnob = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  /* Centred on the bar: 6px of padding below it, 2px of bar, less half the knob. */
  bottom: 3px;
  width: 8px;
  height: 8px;
  margin-left: -4px;
  border-radius: 50%;
  background-color: var(--accent);
  pointer-events: none;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity 0.12s ease;
`;

/**
 * Where a scrub would land: the time (or times) on one line, and under them the
 * transcript at that point. All of it reads off the pointer rather than the
 * playhead — it is a preview of the seek, not a report of it.
 *
 * Carries the shared modal tooltip style inline (both lists live inside the
 * clip preview); the `clamp` on `left` is what keeps the readout whole at
 * either end of the tracker.
 */
const ScrubReadout = styled.div`
  position: absolute;
  bottom: 16px;
  /* Hugs the times line: a fixed width leaves slack either side of it, which
     reads as an oversized box with a short line in it. The transcript strip
     under it is a percentage, so it fills this rather than setting it. */
  width: max-content;
  box-sizing: border-box;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: nowrap;
  pointer-events: none;
`;

/** The times: once in the finished reel, once inside this clip. */
const Times = styled.div`
  font-variant-numeric: tabular-nums;
`;

/**
 * "Reel" / "Clip" — which timeline each figure is on. Bright grey and bold so
 * the label reads as the key and the figure beside it as the value.
 */
const TimeLabel = styled.span`
  color: var(--text-secondary);
  font-weight: var(--font-weight-bold);
`;

/** The dot between them — a join, so it sits back from the times themselves. */
const Separator = styled.span`
  margin: 0 5px;
  color: var(--text-muted);
`;

/* Full-bleed over the frame (the clip cards' hover-overlay idiom), hidden until
   the row is hovered or this row is playing.

   The dimming scrim comes on with the pointer and not with playback: the frame
   plays the actual video, and a 40% black over it for as long as it runs would
   leave the row showing a dimmed clip rather than the clip. So the pause icon
   is held over the running video on its own, carrying a shadow to stay legible
   over whatever frame is underneath it. */
